import { useCallback, useEffect, useMemo, useState } from 'react';
import { UMButton, UMFormStyles, UMIcon } from '../../urbanmap/_components';
import { PlanLogo } from '../proposition-c/ui';
import { calibrateRatios, centerOf, countModifiedRatios, defaultContract, inPerimeter, isValidContract, nearestStation, ratiosForDegrees, stationOf, REF_DJCLIM, REF_DJU } from './domain';
import { useCreateProject } from './hooks';
import { alignToPerf } from './perf';
import { StepPerimeter, type PerimeterDraft } from './StepPerimeter';
import { StepRecap, type CopyStatus } from './StepRecap';
import { StepSettings } from './StepSettings';
import { PC, UMR } from './tokens';
import type { DegreeDays, DjuSettings, Perimeter, RatioTable as Ratios, Referential } from './types';

interface Props {
  referential: Referential;
  onCancel: () => void;
  onOpen: (projectId: string) => void;
}

const STEPS = ['Projet et périmètre', 'Mes ratios', 'Récapitulatif'];

function Stepper({ current }: { current: number }) {
  return (
    <ol style={{ display: 'flex', gap: 8, listStyle: 'none', margin: 0, padding: '12px 24px', borderBottom: `1px solid ${UMR.border}`, background: UMR.grey100 }}>
      {STEPS.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li key={s} aria-current={active ? 'step' : undefined} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: active || done ? UMR.textStrong : UMR.textSecondary, fontWeight: active ? 700 : 400 }}>
            <span style={{ width: 24, height: 24, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, background: done || active ? PC.accent : UMR.white, color: done || active ? UMR.white : UMR.textSecondary, border: `1px solid ${done || active ? PC.accent : UMR.border}` }}>
              {done ? <UMIcon name="check" size={16} color={UMR.white} /> : i + 1}
            </span>
            {s}
            {i < STEPS.length - 1 && <span style={{ width: 40, height: 1, background: UMR.border, margin: '0 4px' }} />}
          </li>
        );
      })}
    </ol>
  );
}

/** Assistant de création, plein écran : périmètre → météo et ratios → récapitulatif. */
export function CreateWizard({ referential, onCancel, onOpen }: Props) {
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<PerimeterDraft>({
    name: '', kind: 'new', networkIds: [], contract: defaultContract(),
    mode: 'communes', communes: [], shape: 'rectangle', points: [], closed: false, drawing: false,
  });
  const [stationId, setStationId] = useState(() => stationOf(referential.defaultDju).id);
  const [stationPicked, setStationPicked] = useState(false);
  const station = useMemo(() => stationOf({ stationId, reference: 0 }), [stationId]);
  /** Degrés-jours retenus pour la station : standards, corrigeables à l'étape 3. */
  const [stationDegrees, setStationDegrees] = useState<DegreeDays>(() => {
    const s = stationOf(referential.defaultDju);
    return { dju: s.dju, djclim: s.djclim };
  });
  const chooseStation = (id: string) => {
    const s = referential.stations.find((x) => x.id === id);
    if (!s) return;
    setStationId(id);
    setStationPicked(true);
    setStationDegrees({ dju: s.dju, djclim: s.djclim });
  };

  /** Degrés-jours auxquels le référentiel de ratios est établi. */
  const defaultDegrees = useMemo<DegreeDays>(() => ({ dju: REF_DJU, djclim: REF_DJCLIM }), []);
  const [myDegrees, setMyDegrees] = useState<DegreeDays>(defaultDegrees);
  /** Référentiel recalé sur ses coefficients de performance (un seul % par période, tous postes). */
  const baseRatios = useMemo(() => alignToPerf(referential.defaultRatios), [referential.defaultRatios]);
  const [myRatios, setMyRatios] = useState<Ratios>(() => alignToPerf(referential.defaultRatios));
  /** Mes ratios tels qu'appliqués au projet (snapshot validé par « Appliquer à la station »). */
  const [applied, setApplied] = useState<{ degrees: DegreeDays; ratios: Ratios }>(
    () => ({ degrees: defaultDegrees, ratios: alignToPerf(referential.defaultRatios) }),
  );
  /** Mes ratios n'alimentent le projet qu'après un premier « Appliquer à la station ». */
  const [appliedOnce, setAppliedOnce] = useState(false);

  const dju = useMemo<DjuSettings>(() => ({ stationId, reference: stationDegrees.dju }), [stationId, stationDegrees.dju]);
  /** Référentiel calibré sur les degrés-jours retenus : sert de base de comparaison. */
  const stationRatios = useMemo(() => ratiosForDegrees(stationDegrees.dju, stationDegrees.djclim), [stationDegrees]);
  /** Ratios réellement utilisés : mes ratios appliqués, calibrés sur les DJ de la station. */
  const ratios = useMemo(
    () => calibrateRatios(applied.ratios, applied.degrees, stationDegrees),
    [applied, stationDegrees],
  );
  const pending = !appliedOnce
    || countModifiedRatios(applied.ratios, myRatios) > 0
    || applied.degrees.dju !== myDegrees.dju
    || applied.degrees.djclim !== myDegrees.djclim;
  const custom = countModifiedRatios(applied.ratios, baseRatios) > 0
    || applied.degrees.dju !== defaultDegrees.dju
    || applied.degrees.djclim !== defaultDegrees.djclim;

  const [ratiosValid, setRatiosValid] = useState(true);
  const [status, setStatus] = useState<CopyStatus>('idle');
  const [progress, setProgress] = useState(0);
  const create = useCreateProject();

  const patch = useCallback((p: Partial<PerimeterDraft>) => setDraft((d) => ({ ...d, ...p })), []);
  const perimeter = useMemo<Perimeter>(
    () => (draft.mode === 'communes' ? { kind: 'communes', communes: draft.communes } : { kind: 'drawn', polygon: draft.closed ? draft.points : [] }),
    [draft.mode, draft.communes, draft.points, draft.closed],
  );
  const included = useMemo(() => referential.buildings.filter((b) => inPerimeter(b, perimeter)), [referential.buildings, perimeter]);
  const includedIds = useMemo(() => new Set(included.map((b) => b.id)), [included]);
  /** Tant que l'utilisateur n'a pas choisi, la station la plus proche du périmètre est proposée. */
  const center = useMemo(() => centerOf(included.map((b) => b.centroid)), [included]);
  useEffect(() => {
    if (stationPicked || !center) return;
    const near = nearestStation(center, referential.stations);
    setStationId(near.id);
    setStationDegrees({ dju: near.dju, djclim: near.djclim });
  }, [center, stationPicked, referential.stations]);
  const perimeterLabel = perimeter.kind === 'drawn' ? 'Zone dessinée à la main' : draft.communes.map((c) => referential.communes.find((x) => x.insee === c)?.name ?? c).join(', ');
  const networkNames = referential.networks.filter((n) => draft.networkIds.includes(n.id)).map((n) => n.name);

  useEffect(() => {
    if (status !== 'copying') return undefined;
    const t = window.setInterval(() => setProgress((p) => Math.min(create.isSuccess ? 100 : 90, p + 8)), 180);
    return () => window.clearInterval(t);
  }, [status, create.isSuccess]);
  useEffect(() => { if (status === 'copying' && progress >= 100) setStatus('done'); }, [status, progress]);

  const launch = () => {
    setStatus('copying');
    setProgress(0);
    create.mutate(
      {
        name: draft.name.trim(), kind: draft.kind, networkIds: draft.networkIds, contract: draft.contract,
        perimeter, ratios, dju, usesDefaults: !custom,
      },
      { onError: () => setStatus('error') },
    );
  };

  const step0Valid = !!draft.name.trim()
    && included.length > 0
    && !draft.drawing
    && isValidContract(draft.contract)
    && (draft.kind === 'new' || draft.networkIds.length > 0);
  const canNext = [step0Valid, ratiosValid && appliedOnce, true][step] ?? true;
  const busy = status === 'copying';

  return (
    <div data-comment-view={`creation-step-${step}`} style={{ position: 'absolute', inset: 0, zIndex: 40, display: 'flex', flexDirection: 'column', background: UMR.white, fontFamily: UMR.font, color: UMR.black }}>
      <UMFormStyles />
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 24px', borderBottom: `1px solid ${UMR.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <PlanLogo size={32} />
          <h2 style={{ margin: 0, fontSize: 20, lineHeight: '28px', fontWeight: 700 }}>Nouveau projet réseau de chaleur</h2>
        </div>
        <UMButton variant="secondary" icon="close" disabled={busy} onClick={onCancel}>Quitter</UMButton>
      </header>
      <Stepper current={step} />

      <div style={{ flex: 1, minHeight: 0 }}>
        {step === 0 && (
          <StepPerimeter
            referential={referential}
            draft={draft}
            onChange={patch}
            included={included}
            includedIds={includedIds}
            stationId={stationId}
            onStationSelect={chooseStation}
          />
        )}
        {step === 1 && (
          <StepSettings
            referential={referential}
            defaultRatios={baseRatios}
            station={station}
            stationDegrees={stationDegrees}
            onStationDegreesChange={setStationDegrees}
            myDegrees={myDegrees}
            myRatios={myRatios}
            appliedDegrees={applied.degrees}
            defaultDegrees={defaultDegrees}
            onMyDegreesChange={setMyDegrees}
            onMyRatiosChange={setMyRatios}
            projectRatios={ratios}
            stationRatios={stationRatios}
            pending={pending}
            applied={appliedOnce}
            onApply={() => { setApplied({ degrees: myDegrees, ratios: myRatios }); setAppliedOnce(true); }}
            onRatiosValidity={setRatiosValid}
          />
        )}
        {step === 2 && (
          <StepRecap
            name={draft.name.trim()}
            kind={draft.kind}
            networkNames={networkNames}
            contract={draft.contract}
            perimeterLabel={perimeterLabel}
            buildingCount={included.length}
            stationName={stationOf(dju).name}
            dju={stationDegrees.dju}
            djclim={stationDegrees.djclim}
            degreesEdited={stationDegrees.dju !== station.dju || stationDegrees.djclim !== station.djclim}
            status={status}
            progress={progress}
          />
        )}
      </div>

      <footer style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 24px', borderTop: `1px solid ${UMR.border}` }}>
        <span style={{ fontSize: 12, color: UMR.textSecondary }}>
          {step === 0 ? 'Étape 1 sur 3 · nom, périmètre, station météo, type de projet et période du contrat'
            : step === 1 ? 'Étape 2 sur 3 · mes ratios, puis calibrage sur la station retenue'
              : 'Vérifiez avant de lancer la copie'}
        </span>
        <span style={{ flex: 1 }} />
        {status === 'done' ? (
          <UMButton icon="arrow_forward" onClick={() => create.data && onOpen(create.data.id)}>Ouvrir le projet</UMButton>
        ) : (
          <>
            <UMButton variant="secondary" disabled={busy} onClick={onCancel}>Annuler</UMButton>
            {step > 0 && <UMButton variant="secondary" icon="arrow_back" disabled={busy} onClick={() => setStep((s) => s - 1)}>Précédent</UMButton>}
            {step === 0 && (
              <UMButton icon="arrow_forward" disabled={!step0Valid} onClick={() => setStep(1)}>Suivant</UMButton>
            )}
            {step === 1 && <UMButton icon="arrow_forward" disabled={!canNext} onClick={() => setStep(2)}>Vérifier</UMButton>}
            {step === 2 && <UMButton icon="content_copy" disabled={busy} onClick={launch}>{status === 'error' ? 'Réessayer' : 'Créer le projet'}</UMButton>}
          </>
        )}
      </footer>
    </div>
  );
}
